import React, { useEffect } from 'react';
import { formatMoney } from '../../utils/formatMoney';
import { lostCatalogSelection } from '../../utils/catalogSelection';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Home, Package, Star } from 'lucide-react';
import { useCatalogPrefsV2 } from '../../utils/catalogPrefsV2';
import { keepTouchClickOn } from '../../utils/ghostClick';
import type { Product, Category } from '../../types/order';
import {
  categoryColorClasses,
  categoryToneOf,
  getGridClass,
  getCategoryFontSize,
  getProductFontSize,
  getProductPriceFontSize,
  getCategoryMinHeight,
  getProductMinHeight,
} from '../../utils/catalogHelpers';
import { catalogTone } from '../../utils/catalogColors';
import ProductCatalogV2 from './ProductCatalogV2';

export type CatalogView = 'categories' | 'subcategories' | 'products';

interface ProductCatalogProps {
  categories: Category[];
  products: Product[];
  loading: boolean;
  currentView: CatalogView;
  selectedCategory: number | null;
  selectedSubcategory: number | null;
  searchTerm: string;
  categoryColumns: number;
  productColumns: number;

  // Callbacks
  onCategorySelect: (category: Category) => void;
  onSubcategorySelect: (subcategory: Category | null) => void;
  onProductClick: (product: Product) => void;
  onProductMouseDown: (product: Product) => void;
  onProductMouseUp: () => void;

  // Τιμή πλακιδίου ανά κανάλι — ο γονέας περνά productDisplayPrice(p, <κανάλι>) (utils/pricing)
  getProductPrice: (product: Product) => number;

  // i18n namespace for labels
  translationNamespace?: string;

  /** Gross per-unit price of the configured code-16 (single-use plastic) fee
   *  product. When > 0 and a product has auto_plastic_fee_count > 0, the tile
   *  shows the breakdown "€X,XX + €Y,YY τέλη" so the cashier sees the full
   *  customer-paid price upfront. Zero/undefined → no breakdown. */
  plasticFeeUnitGross?: number;

  /** «Αγαπημένα» (take away): ids με τη σειρά τους. Η κατηγορία εμφανίζεται
   *  πρώτη μόνο όταν δοθεί και το onFavoritesSelect ΚΑΙ έστω ένα από αυτά είναι
   *  διαθέσιμο (αποσυρμένα/κρυφά δεν μετράνε). */
  favoriteProductIds?: number[];
  /** Ο ταμίας είναι μέσα στην κατηγορία «Αγαπημένα» (currentView = 'products'). */
  favoritesSelected?: boolean;
  onFavoritesSelect?: () => void;

  /** «Νέα εμφάνιση»: κατηγορίες επάνω ως φίλτρο + προϊόντα από κάτω (ProductCatalogV2).
   *  Τη ζητά μόνο το take away· delivery και dine-in μένουν κλασικά. */
  look?: 'classic' | 'v2';
  /** Νέα εμφάνιση: «Όλα» με επιλεγμένη κατηγορία πρέπει να καθαρίσει και την κατηγορία
   *  (το onSubcategorySelect(null) την κρατά). */
  onAllProductsSelect?: () => void;
  /** Νέα εμφάνιση: πόσα τεμάχια κάθε προϊόντος είναι ήδη στο καλάθι (μετρητής «×2» στην κάρτα). */
  basketCounts?: Record<number, number>;
  /** Διάταξη «σε βήματα» μέσα στις νέες οθόνες (12/10/2026): ο κατάλογος βάζει μόνος του το «Πίσω»
   *  πάνω από τα προϊόντα/υποκατηγορίες, γιατί η νέα κεφαλίδα δεν έχει τα κουμπιά της παλιάς. */
  onBack?: () => void;
  /** Μαζί με το onBack: το «σπιτάκι» δίπλα στο «Πίσω» — κατευθείαν στις κατηγορίες από οποιοδήποτε βάθος,
   *  όπως στην κεφαλίδα της παλιάς οθόνης (χάθηκε με το 51b806ce, ξαναμπήκε 28/10/2026). */
  onHome?: () => void;
}

const ProductCatalog: React.FC<ProductCatalogProps> = ({
  categories,
  products: allProducts,
  loading,
  currentView: currentViewProp,
  selectedCategory: selectedCategoryProp,
  selectedSubcategory: selectedSubcategoryProp,
  searchTerm,
  categoryColumns,
  productColumns,
  onCategorySelect,
  onSubcategorySelect,
  onProductClick,
  onProductMouseDown,
  onProductMouseUp,
  getProductPrice,
  translationNamespace = 'takeaway',
  plasticFeeUnitGross = 0,
  favoriteProductIds,
  favoritesSelected = false,
  onFavoritesSelect,
  look = 'classic',
  onAllProductsSelect,
  basketCounts,
  onBack,
  onHome,
}) => {
  const { t } = useTranslation([translationNamespace, 'products', 'common']);
  // Μέσα στη νέα οθόνη («σε βήματα», onBack) τα πλακίδια παίρνουν το χρώμα της ρύθμισης συσκευής
  // (CashierV2.css §4, data-color)· στην παλιά οθόνη μένουν όπως ήταν.
  const catalogPrefs = useCatalogPrefsV2();
  const tileColor = onBack && catalogPrefs.categoryColor !== 'none' ? catalogPrefs.categoryColor : undefined;

  // Categories flagged hidden (e.g. seasonal) must not surface anywhere in the
  // POS — not as tiles, not via search, and neither must their products. is_hidden
  // is independent of is_active, so hidden categories still arrive here; we drop
  // them (and any hidden subcategory, plus subcategories of a hidden parent)
  // centrally for every POS surface. The admin keeps showing them.
  const hiddenCategoryIds = new Set<number>();
  categories.forEach(c => {
    if (c.is_hidden) hiddenCategoryIds.add(c.id);
    (c.subcategories || []).forEach(s => {
      if (c.is_hidden || s.is_hidden) hiddenCategoryIds.add(s.id);
    });
  });
  const products = allProducts.filter(p => !hiddenCategoryIds.has(p.category_id));

  // Αγαπημένα με τη σειρά της λίστας, μόνο όσα υπάρχουν στον (ορατό) κατάλογο.
  const favoriteProducts = (() => {
    if (!onFavoritesSelect || !favoriteProductIds?.length) return [];
    const byId = new Map(products.map(p => [p.id, p]));
    return favoriteProductIds.map(id => byId.get(id)).filter((p): p is Product => !!p);
  })();

  // Η επιλεγμένη κατηγορία ΧΑΘΗΚΕ από τον κατάλογο (24/10/2026: «Καθαρισμός ημέρας» ενώ ο ταμίας είναι μέσα
  // στα «Πιάτα ημέρας» → «0 προϊόντα» ώσπου να πατήσει άλλη). Ο κατάλογος δείχνει ΑΜΕΣΩΣ την αρχική του
  // προβολή (υποκατηγορία που χάθηκε → η κατηγορία της) και ενημερώνει τον γονέα με τα callbacks που ήδη δίνει,
  // ώστε να μην τον «ξαναβάλει» εκεί όταν η κατηγορία ξαναφανεί. Μία φορά για όλες τις οθόνες (Take Away,
  // delivery, τραπέζια): η επιλογή μένει στον γονέα. Όσο φορτώνει, τίποτα (ο κατάλογος ίσως είναι μισός).
  const lost = loading || favoritesSelected
    ? null
    : lostCatalogSelection(categories, selectedCategoryProp, selectedSubcategoryProp, currentViewProp);
  const lostSub = lost?.kind === 'subcategory' ? lost.category : null;
  const currentView: CatalogView = !lost
    ? currentViewProp
    : lostSub ? (lostSub.subcategories?.length ? 'subcategories' : 'products') : 'categories';
  const selectedCategory = !lost ? selectedCategoryProp : lostSub ? lostSub.id : null;
  const selectedSubcategory = lost ? null : selectedSubcategoryProp;
  const searching = !!searchTerm.trim();
  const lostKey = lost ? `${lost.kind}:${selectedCategoryProp}:${selectedSubcategoryProp}:${currentViewProp}:${searching}` : null;
  useEffect(() => {
    // Με αναζήτηση η επιλογή δεν μετρά — και τα callbacks του γονέα θα έσβηναν ό,τι πληκτρολογεί ο ταμίας.
    if (!lost || searching) return;
    if (lostSub) onCategorySelect(lostSub);
    else if (look === 'v2') {
      // Η αρχή της νέας εμφάνισης: «Αγαπημένα» αν υπάρχουν, αλλιώς «Όλα» (ProductCatalogV2).
      if (onFavoritesSelect && favoriteProducts.length > 0) onFavoritesSelect();
      else onAllProductsSelect?.();
    } else if (currentViewProp !== 'categories') {
      // «Σε βήματα»: το «Πίσω» του γονέα γυρίζει στις κατηγορίες (χωρίς onBack — παλιά οθόνη — η κεφαλίδα
      // του γονέα έχει δικό της «Πίσω»· ο κατάλογος πάντως δείχνει ήδη τις κατηγορίες).
      onBack?.();
    }
    // Μόνο όταν αλλάζει η «χαμένη» επιλογή: τα callbacks είναι νέα σε κάθε render του γονέα.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lostKey]);

  // Per-product fee addon for catalog display. Returns the gross amount of the
  // auto-triggered plastic fee for one unit of the product (or 0 when there's
  // no trigger / no fee product configured).
  const getPlasticFeeAddon = (product: Product): number => {
    const count = product.auto_plastic_fee_count || 0;
    if (count <= 0 || plasticFeeUnitGross <= 0) return 0;
    return Math.round(count * plasticFeeUnitGross * 100) / 100;
  };

  const getSelectedCategory = () => categories.find(c => c.id === selectedCategory);

  const getCategoryProductCount = (categoryId: number) => {
    const category = categories.find(c => c.id === categoryId);
    const subcategoryIds = category?.subcategories?.map(s => s.id) || [];
    return products.filter(p => p.category_id === categoryId || subcategoryIds.includes(p.category_id)).length;
  };

  const getSubcategoryProductCount = (subcategoryId: number) => {
    return products.filter(p => p.category_id === subcategoryId).length;
  };

  // While searching, results span every product in the store regardless of
  // which view the user is currently in. The categories/subcategories grids
  // are hidden — search jumps straight to flat product results.
  const isSearching = !!searchTerm.trim();
  const effectiveView: CatalogView = isSearching ? 'products' : currentView;

  const filteredProducts = (() => {
    let filtered = products;
    if (!isSearching) {
      if (favoritesSelected) {
        filtered = favoriteProducts;
      } else if (selectedSubcategory) {
        filtered = products.filter(p => p.category_id === selectedSubcategory);
      } else if (selectedCategory) {
        const parent = categories.find(c => c.id === selectedCategory);
        const subcategoryIds = parent?.subcategories?.map(s => s.id) || [];
        const validIds = [selectedCategory, ...subcategoryIds];
        filtered = products.filter(p => validIds.includes(p.category_id));
      }
    }
    if (searchTerm) {
      const needle = searchTerm.toLowerCase();
      filtered = filtered.filter(p =>
        p.name.toLowerCase().includes(needle) ||
        p.description?.toLowerCase().includes(needle)
      );
    }
    return filtered;
  })();

  // Products that don't belong to any user-visible category. They live in a
  // hidden per-store "system" category in the DB, but in the UI we render them
  // as bare product cards alongside the named category cards (or alone, if
  // the store skipped categories entirely).
  const systemCategoryIds = new Set(categories.filter(c => c.is_system).map(c => c.id));
  const uncategorizedProducts = products.filter(p => systemCategoryIds.has(p.category_id));

  if (look === 'v2') {
    return (
      <ProductCatalogV2
        categories={categories.filter(c => !c.is_system && !c.is_hidden)}
        products={products}
        favoriteProducts={favoriteProducts}
        loading={loading}
        currentView={currentView}
        selectedCategory={selectedCategory}
        selectedSubcategory={selectedSubcategory}
        favoritesSelected={favoritesSelected}
        searchTerm={searchTerm}
        categoryColumns={categoryColumns}
        productColumns={productColumns}
        onCategorySelect={onCategorySelect}
        onSubcategorySelect={onSubcategorySelect}
        onFavoritesSelect={onFavoritesSelect}
        onAllProductsSelect={onAllProductsSelect}
        basketCounts={basketCounts}
        onProductClick={onProductClick}
        onProductMouseDown={onProductMouseDown}
        onProductMouseUp={onProductMouseUp}
        getProductPrice={getProductPrice}
        getPlasticFeeAddon={getPlasticFeeAddon}
        translationNamespace={translationNamespace}
      />
    );
  }

  const backTitle = favoritesSelected
    ? t(`${translationNamespace}:favorites`, { defaultValue: 'Αγαπημένα' })
    : [getSelectedCategory()?.name, categories.flatMap(c => c.subcategories || []).find(s => s.id === selectedSubcategory)?.name]
      .filter(Boolean).join(' · ');

  return (
    <div className="cashier-catalog flex-1 overflow-y-auto p-4 bg-surface-1">
      {/* «Σε βήματα» μέσα στη νέα οθόνη: «Πίσω» + «Αρχή» + πού βρίσκεται ο ταμίας */}
      {onBack && !isSearching && effectiveView !== 'categories' && (
        <div className="mb-3 flex items-center gap-3 min-w-0">
          <button type="button" onClick={onBack}
            className="h-11 px-4 shrink-0 rounded-xl border border-line bg-surface-2 text-t1 font-semibold flex items-center gap-1.5 hover:bg-surface-3 active:scale-[0.98] touch-manipulation">
            <ChevronLeft aria-hidden="true" className="h-5 w-5" />
            {t('common:back')}
          </button>
          {onHome && (
            <button type="button" onClick={onHome}
              aria-label={t('common:home')} title={t('common:home')}
              className="h-11 w-11 shrink-0 rounded-xl border border-line bg-surface-2 text-t1 flex items-center justify-center hover:bg-surface-3 active:scale-[0.98] touch-manipulation">
              <Home aria-hidden="true" className="h-5 w-5" />
            </button>
          )}
          <span className="text-base font-semibold text-t1 truncate">{backTitle}</span>
        </div>
      )}
      {/* ============== CATEGORIES VIEW ============== */}
      {effectiveView === 'categories' && (
        <div className={`grid ${getGridClass(categoryColumns)} gap-3`}>
          {/* «Αγαπημένα» — πάντα πρώτη, μόνο όταν υπάρχει έστω ένα διαθέσιμο. */}
          {!searchTerm && favoriteProducts.length > 0 && (
            <button
              onClick={onFavoritesSelect}
              data-tone="amber"
              data-color={tileColor}
              className={`cashier-category-tile cashier-favorites-tile relative rounded-xl text-center transition-all duration-150 active:scale-[0.96] flex flex-col items-center justify-center p-4 ${getCategoryMinHeight(categoryColumns)} bg-amber-500 hover:bg-amber-400 shadow-lg shadow-amber-500/30`}
            >
              <Star aria-hidden="true" className="cashier-favorites-star h-5 w-5 mb-1 text-white fill-current" />
              <h3 className={`font-bold ${getCategoryFontSize(categoryColumns)} text-white leading-tight line-clamp-2 uppercase tracking-wide`}>
                {t(`${translationNamespace}:favorites`, { defaultValue: 'Αγαπημένα' })}
              </h3>
              <span className="mt-2 text-xs font-medium text-white/80">
                ({favoriteProducts.length})
              </span>
              <ChevronRight aria-hidden="true" className="cashier-category-arrow hidden absolute bottom-2 right-2 h-4 w-4" />
            </button>
          )}

          {categories
            .filter(cat => !cat.is_system)
            .filter(cat => !cat.is_hidden)
            .filter(cat => !searchTerm || cat.name.toLowerCase().includes(searchTerm.toLowerCase()))
            .map((category, index) => {
              // Ρητός τόνος του διαχειριστή (category.color), αλλιώς ο αυτόματος από το όνομα.
              const color = categoryColorClasses(category, index);
              const count = getCategoryProductCount(category.id);
              return (
                <button
                  key={category.id}
                  onClick={() => onCategorySelect(category)}
                  data-tone={categoryToneOf(category, index)} data-color={tileColor}
                  className={`cashier-category-tile relative rounded-xl text-center transition-all duration-150 active:scale-[0.96] flex flex-col items-center justify-center p-4 ${getCategoryMinHeight(categoryColumns)} ${color} shadow-lg shadow-black/10 dark:shadow-black/20`}
                >
                  <h3 className={`font-bold ${getCategoryFontSize(categoryColumns)} text-white leading-tight line-clamp-2 uppercase tracking-wide`}>
                    {category.name}
                  </h3>
                  {count > 0 && (
                    <span className="mt-2 text-xs font-medium text-white/70">
                      ({count})
                    </span>
                  )}
                  <ChevronRight aria-hidden="true" className={`cashier-category-arrow absolute bottom-2 right-2 h-4 w-4 text-white/40 ${category.subcategories?.length ? '' : 'hidden'}`} />
                </button>
              );
            })}

          {/* Uncategorized products — rendered inline in the categories grid so
              users with mixed catalogs (some categorized, some not) see both
              at once, and stores with no categories at all skip the drill-in. */}
          {uncategorizedProducts
            .filter(p => !searchTerm
              || p.name.toLowerCase().includes(searchTerm.toLowerCase())
              || p.description?.toLowerCase().includes(searchTerm.toLowerCase()))
            .map(product => {
              const price = getProductPrice(product);
              const showPriceLine =
                product.is_open_price || product.is_weight_variable || price > 0;
              return (
                <button
                  key={`uncat-${product.id}`}
                  data-tone={catalogTone(product.color) ?? undefined}
                  onClick={() => onProductClick(product)}
                  onMouseDown={() => onProductMouseDown(product)}
                  onMouseUp={onProductMouseUp}
                  onMouseLeave={onProductMouseUp}
                  onTouchStart={() => onProductMouseDown(product)}
                  onTouchEnd={onProductMouseUp}
                  onPointerUp={e => keepTouchClickOn(e, e.currentTarget)}
                  className={`cashier-product-tile rounded-xl text-center transition-all duration-150 active:scale-[0.96] flex flex-col items-center justify-center p-4 ${getCategoryMinHeight(categoryColumns)} border ${
                    product.is_open_price
                      ? 'bg-surface-2 dark:bg-surface-2/80 border-emerald-500/30 hover:border-emerald-400/60'
                      : product.is_weight_variable
                      ? 'bg-surface-2 dark:bg-surface-2/80 border-amber-500/30 hover:border-amber-400/60'
                      : 'bg-surface-2 dark:bg-surface-2/80 border-line-strong hover:border-slate-400'
                  } shadow-lg shadow-black/10 dark:shadow-black/20`}
                >
                  <h3 className={`font-bold ${getCategoryFontSize(categoryColumns)} text-t1 leading-tight line-clamp-2 uppercase tracking-wide`}>
                    {product.name}
                  </h3>
                  {showPriceLine && (
                    <span className={`mt-2 ${getProductPriceFontSize(categoryColumns)} font-bold ${
                      product.is_open_price ? 'text-emerald-600 dark:text-emerald-400' : product.is_weight_variable ? 'text-amber-700 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
                    }`}>
                      {product.is_open_price
                        ? (
                          <>
                            {t(`${translationNamespace}:openPrice`, { defaultValue: t(`${translationNamespace}:openPriceShort`, { defaultValue: 'Open' }) })}
                            {/* «Βάρος + τιμή κιλού»: ελεύθερη τιμή ΑΝΑ κιλό/λίτρο. */}
                            {product.is_weight_variable && <span className="text-xs text-t3">/{product.selling_unit === 'LT' ? 'lt' : 'kg'}</span>}
                          </>
                        )
                        : (
                          <>
                            {formatMoney(price)}
                            {product.is_weight_variable && (
                              <span className="text-xs text-t3">
                                /{product.selling_unit === 'LT' ? 'lt' : 'kg'}
                              </span>
                            )}
                            {getPlasticFeeAddon(product) > 0 && (
                              <span className="text-xs text-t3 ml-1 font-normal">
                                + {formatMoney(getPlasticFeeAddon(product))} τέλη
                              </span>
                            )}
                          </>
                        )
                      }
                    </span>
                  )}
                </button>
              );
            })}

          {/* "All products" tile — always last in the categories view, opens
              the products list with no category filter so the cashier can
              jump to a flat list when they don't remember the category. */}
          {!searchTerm && products.length > 0 && (
            <button
              onClick={() => onSubcategorySelect(null)}
              className={`cashier-category-tile cashier-all-products relative rounded-xl text-center transition-all duration-150 active:scale-[0.96] flex flex-col items-center justify-center p-4 ${getCategoryMinHeight(categoryColumns)} bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-600/30`}
            >
              <h3 className={`font-bold ${getCategoryFontSize(categoryColumns)} text-white leading-tight line-clamp-2 uppercase tracking-wide`}>
                {t('products:allProducts', { defaultValue: t(`${translationNamespace}:allProducts`, { defaultValue: 'All products' }) })}
              </h3>
              <span className="mt-2 text-xs font-medium text-white/80">
                ({products.length})
              </span>
              <ChevronRight aria-hidden="true" className="cashier-category-arrow hidden absolute bottom-2 right-2 h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {/* ============== SUBCATEGORIES VIEW ============== */}
      {effectiveView === 'subcategories' && (
        <div className="space-y-3">
          <div className={`grid ${getGridClass(categoryColumns)} gap-3`}>
            {getSelectedCategory()?.subcategories
              ?.filter(sub => !sub.is_hidden)
              .filter(sub => !searchTerm || sub.name.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((subcategory, index) => {
                const color = categoryColorClasses(subcategory, index);
                const count = getSubcategoryProductCount(subcategory.id);
                return (
                  <button
                    key={subcategory.id}
                    onClick={() => onSubcategorySelect(subcategory)}
                    data-tone={categoryToneOf(subcategory, index)} data-color={tileColor}
                    className={`cashier-category-tile relative rounded-xl text-center transition-all duration-150 active:scale-[0.96] flex flex-col items-center justify-center p-4 ${getCategoryMinHeight(categoryColumns)} ${color} shadow-lg shadow-black/10 dark:shadow-black/20`}
                  >
                    <h3 className={`font-bold ${getCategoryFontSize(categoryColumns)} text-white leading-tight line-clamp-2 uppercase tracking-wide`}>
                      {subcategory.name}
                    </h3>
                    {count > 0 && (
                      <span className="mt-2 text-xs font-medium text-white/70">
                        ({count})
                      </span>
                    )}
                    <ChevronRight aria-hidden="true" className="cashier-category-arrow hidden absolute bottom-2 right-2 h-4 w-4" />
                  </button>
                );
              })}

            {/* Products that live directly under the parent category (not in
                any subcategory). Rendered inline with subcategory cards so a
                category like "Coffees" with 3 sub-types AND one off-menu item
                shows everything in one view without forcing the user into
                "All products". */}
            {selectedCategory && products
              .filter(p => p.category_id === selectedCategory)
              .filter(p => !searchTerm
                || p.name.toLowerCase().includes(searchTerm.toLowerCase())
                || p.description?.toLowerCase().includes(searchTerm.toLowerCase()))
              .map(product => {
                const price = getProductPrice(product);
                const showPriceLine =
                  product.is_open_price || product.is_weight_variable || price > 0;
                return (
                  <button
                    key={`direct-${product.id}`}
                    data-tone={catalogTone(product.color) ?? undefined}
                    onClick={() => onProductClick(product)}
                    onMouseDown={() => onProductMouseDown(product)}
                    onMouseUp={onProductMouseUp}
                    onMouseLeave={onProductMouseUp}
                    onTouchStart={() => onProductMouseDown(product)}
                    onTouchEnd={onProductMouseUp}
                    onPointerUp={e => keepTouchClickOn(e, e.currentTarget)}
                    className={`cashier-product-tile rounded-xl text-center transition-all duration-150 active:scale-[0.96] flex flex-col items-center justify-center p-4 ${getCategoryMinHeight(categoryColumns)} border ${
                      product.is_open_price
                        ? 'bg-surface-2 dark:bg-surface-2/80 border-emerald-500/30 hover:border-emerald-400/60'
                        : product.is_weight_variable
                        ? 'bg-surface-2 dark:bg-surface-2/80 border-amber-500/30 hover:border-amber-400/60'
                        : 'bg-surface-2 dark:bg-surface-2/80 border-line-strong hover:border-slate-400'
                    } shadow-lg shadow-black/10 dark:shadow-black/20`}
                  >
                    <h3 className={`font-bold ${getCategoryFontSize(categoryColumns)} text-t1 leading-tight line-clamp-2 uppercase tracking-wide`}>
                      {product.name}
                    </h3>
                    {showPriceLine && (
                      <span className={`mt-2 ${getProductPriceFontSize(categoryColumns)} font-bold ${
                        product.is_open_price ? 'text-emerald-600 dark:text-emerald-400' : product.is_weight_variable ? 'text-amber-700 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
                      }`}>
                        {product.is_open_price
                          ? (
                          <>
                            {t(`${translationNamespace}:openPrice`, { defaultValue: t(`${translationNamespace}:openPriceShort`, { defaultValue: 'Open' }) })}
                            {/* «Βάρος + τιμή κιλού»: ελεύθερη τιμή ΑΝΑ κιλό/λίτρο. */}
                            {product.is_weight_variable && <span className="text-xs text-t3">/{product.selling_unit === 'LT' ? 'lt' : 'kg'}</span>}
                          </>
                        )
                          : (
                            <>
                              {formatMoney(price)}
                              {product.is_weight_variable && (
                                <span className="text-xs text-t3">
                                  /{product.selling_unit === 'LT' ? 'lt' : 'kg'}
                                </span>
                              )}
                            </>
                          )
                        }
                      </span>
                    )}
                  </button>
                );
              })}
          </div>
        </div>
      )}

      {/* ============== PRODUCTS VIEW ============== */}
      {effectiveView === 'products' && (
        <>
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-400"></div>
                <p className="mt-4 text-t3">{t('products:loadingProducts', { defaultValue: 'Loading products...' })}</p>
              </div>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center text-t4">
                <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium text-t3">
                  {t('products:productsNotFound', { defaultValue: t(`${translationNamespace}:noProducts`, { defaultValue: 'No products found' }) })}
                </p>
                <p className="text-sm mt-1 text-t4">
                  {t(`${translationNamespace}:tryOtherCategory`, { defaultValue: t(`${translationNamespace}:noProductsFoundHint`, { defaultValue: 'Try another category' }) })}
                </p>
              </div>
            </div>
          ) : (
            <div className={`grid ${getGridClass(productColumns)} gap-3`}>
              {filteredProducts.map((product) => {
                const price = getProductPrice(product);
                // Hide the price line entirely for products whose base price is
                // 0 — typically "configurable" products where the real price
                // comes from sizes/options. Open-price and weight-variable
                // products keep their own labels regardless.
                const showPriceLine =
                  product.is_open_price || product.is_weight_variable || price > 0;
                return (
                <button
                  key={product.id}
                  data-tone={catalogTone(product.color) ?? undefined}
                  onClick={() => onProductClick(product)}
                  onMouseDown={() => onProductMouseDown(product)}
                  onMouseUp={onProductMouseUp}
                  onMouseLeave={onProductMouseUp}
                  onTouchStart={() => onProductMouseDown(product)}
                  onTouchEnd={onProductMouseUp}
                  onPointerUp={e => keepTouchClickOn(e, e.currentTarget)}
                  className={`cashier-product-tile rounded-xl text-center transition-all duration-150 active:scale-[0.96] flex flex-col items-center justify-center p-3 relative ${getProductMinHeight(productColumns)} border ${
                    product.is_open_price
                      ? 'bg-surface-2 dark:bg-surface-2/80 border-emerald-500/30 hover:border-emerald-400/60'
                      : product.is_weight_variable
                      ? 'bg-surface-2 dark:bg-surface-2/80 border-amber-500/30 hover:border-amber-400/60'
                      : 'bg-surface-2 dark:bg-surface-2/80 border-line-strong hover:border-slate-400'
                  } shadow-lg shadow-black/10 dark:shadow-black/20`}
                >
                  <h3 className={`font-bold ${getProductFontSize(productColumns)} text-t1 leading-tight line-clamp-2 uppercase tracking-wide`}>
                    {product.name}
                  </h3>
                  {showPriceLine && (
                    <span className={`mt-2 ${getProductPriceFontSize(productColumns)} font-bold ${
                      product.is_open_price ? 'text-emerald-600 dark:text-emerald-400' : product.is_weight_variable ? 'text-amber-700 dark:text-amber-400' : 'text-blue-600 dark:text-blue-400'
                    }`}>
                      {product.is_open_price
                        ? (
                          <>
                            {t(`${translationNamespace}:openPrice`, { defaultValue: t(`${translationNamespace}:openPriceShort`, { defaultValue: 'Open' }) })}
                            {/* «Βάρος + τιμή κιλού»: ελεύθερη τιμή ΑΝΑ κιλό/λίτρο. */}
                            {product.is_weight_variable && <span className="text-xs text-t3">/{product.selling_unit === 'LT' ? 'lt' : 'kg'}</span>}
                          </>
                        )
                        : (
                          <>
                            {formatMoney(price)}
                            {product.is_weight_variable && (
                              <span className="text-xs text-t3">
                                /{product.selling_unit === 'LT' ? 'lt' : 'kg'}
                              </span>
                            )}
                            {getPlasticFeeAddon(product) > 0 && (
                              <span className="text-xs text-t3 ml-1 font-normal">
                                + {formatMoney(getPlasticFeeAddon(product))} τέλη
                              </span>
                            )}
                          </>
                        )
                      }
                    </span>
                  )}
                </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProductCatalog;
